import {useEffect, useState} from 'react'
import {useMutation, useQueryClient} from '@tanstack/react-query'
import {Loader} from "@/customComponents/uiComponents/Loader.jsx";
import {addNewCard} from '@img/index.js'
import Modal from '@/customComponents/uiComponents/modal/Modal.jsx'
import ModalHeader from '@/customComponents/uiComponents/modal/ModalHeader.jsx'
import AddCardPopup, {CardsList} from '@/customComponents/editRideComponents/PageModals/ComponentPopups/PaymentMethodsPopup.jsx'
import {tripService, isPaymentMethodUnusableError, PAYMENT_METHOD_UNUSABLE_MESSAGE} from '@/services/tripService'
import {usePaymentMethods, useInvalidatePaymentMethods} from '@/hooks/usePaymentMethods'

export default function RetryHoldButtons({trip}) {
    const queryClient = useQueryClient()
    const [showAttachModal, setShowAttachModal] = useState(false)

    const invalidateAfterHold = () => {
        queryClient.invalidateQueries({queryKey: ['trip', String(trip.id)]})
        queryClient.invalidateQueries({queryKey: ['trips']})
    }

    const retryMutation = useMutation({
        mutationFn: async () => {
            const result = await tripService.retryHold(trip.id)
            if (!result.success) throw new Error(result.error || 'Failed to retry hold')
            return result
        },
        onSuccess: invalidateAfterHold,
    })

    return (
        <>
            <div className="flex justify-end items-start gap-3 mt-6">
                <div className="flex flex-col items-center gap-1">
                    <button type="button" onClick={() => retryMutation.mutate()} disabled={retryMutation.isPending}
                            title="Retry hold funds" aria-label="Retry hold funds"
                            className="cursor-pointer w-10 h-10 shrink-0 rounded-full bg-(--text-primary) flex items-center justify-center p-0 border-0 disabled:opacity-60 disabled:cursor-not-allowed">
                        {retryMutation.isPending ? (
                            <Loader color="#fff"/>
                        ) : (
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                                <polyline points="23 4 23 10 17 10"/>
                                <path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"/>
                            </svg>
                        )}
                    </button>
                    <span className="text-xs">Retry</span>
                </div>
                <div className="flex flex-col items-center gap-1">
                    <button type="button" onClick={() => setShowAttachModal(true)} disabled={retryMutation.isPending}
                            title="Add card" aria-label="Add card"
                            className="cursor-pointer w-10 h-10 shrink-0 rounded-full bg-(--text-primary) flex items-center justify-center p-0 border-0 disabled:opacity-60 disabled:cursor-not-allowed">
                        <img src={addNewCard} alt=""/>
                    </button>
                    <span className="text-xs">Add</span>
                </div>
            </div>
            {retryMutation.isError && (
                <p className="text-(--text-red) text-sm mb-0! mt-2 text-right">{retryMutation.error.message}</p>
            )}

            <AttachCardModal
                isOpen={showAttachModal}
                onClose={() => setShowAttachModal(false)}
                trip={trip}
                onAttached={invalidateAfterHold}
            />
        </>
    )
}

function AttachCardModal({isOpen, onClose, trip, onAttached}) {
    const {data: savedCards = []} = usePaymentMethods()
    const invalidateCards = useInvalidatePaymentMethods()

    const [selectedCardId, setSelectedCardId] = useState(null)
    const [showAddCard, setShowAddCard] = useState(false)

    useEffect(() => {
        if (!isOpen) return
        const tripPmId = trip?.selected_payment_method?.id
        const defaultId = savedCards.find(pm => pm.is_default)?.id ?? savedCards[0]?.id ?? null
        setSelectedCardId(tripPmId ?? defaultId)
    }, [isOpen, savedCards, trip?.selected_payment_method?.id])

    const attachMutation = useMutation({
        mutationFn: async () => {
            if (!selectedCardId) throw new Error('Please select a card.')
            const result = await tripService.attachPaymentMethod(trip.id, {payment_method_id: Number(selectedCardId)})
            // The card is verified before it is attached (owner, active, unexpired, right
            // Square environment). A refused one is refetched away rather than left selected
            // in the list — the listing filters unusable cards out server-side, so the
            // refetch is what removes it and stops a second identical retry.
            if (isPaymentMethodUnusableError(result)) {
                invalidateCards()
                setSelectedCardId(null)
                throw new Error(result.raw?.message || PAYMENT_METHOD_UNUSABLE_MESSAGE)
            }
            if (!result.success) throw new Error(result.error || 'Failed to attach payment method')
            return result
        },
        onSuccess: () => {
            onAttached?.()
            onClose?.()
        },
    })

    const handleCardAdded = (newCard) => {
        invalidateCards()
        if (newCard?.id) setSelectedCardId(newCard.id)
        setShowAddCard(false)
    }

    const handleClose = () => {
        attachMutation.reset()
        onClose?.()
    }

    return (
        <>
            <Modal isOpen={isOpen} onClose={handleClose} className="max-w-137.5!"
                   footer={(
                       <button type="button" onClick={() => attachMutation.mutate()}
                               disabled={!selectedCardId || attachMutation.isPending}
                               className="hopn-add-passenger-button">
                           {attachMutation.isPending ? <Loader color="#fff"/> : 'Confirm & retry hold'}
                       </button>
                   )}>
                <ModalHeader title="Choose payment method" onClose={handleClose}/>

                <p className="text-(--text-secondary) mb-4">Pick a saved card or add a new one to retry the hold.</p>

                <CardsList cards={savedCards} selectedId={selectedCardId} onSelect={setSelectedCardId}/>

                <div className="flex justify-end mt-4">
                    <button type="button" onClick={() => setShowAddCard(true)}
                            className="cursor-pointer w-10 h-10 shrink-0 rounded-full bg-(--text-primary) flex items-center justify-center p-0 border-0">
                        <img src={addNewCard} alt="Add card"/>
                    </button>
                </div>

                {attachMutation.isError && (
                    <p className="text-(--text-red) text-sm mt-4 mb-0!">{attachMutation.error.message}</p>
                )}
            </Modal>

            <AddCardPopup
                isOpen={showAddCard}
                onClose={() => setShowAddCard(false)}
                onCardAdded={handleCardAdded}
            />
        </>
    )
}
